import { z } from 'zod';

/**
 * Communication with merchants (ADR-0153, BR-257): announcements shown as a banner in /manager
 * (optionally e-mailed to the owners), the onboarding e-mail series and the NPS survey.
 */
export const ANNOUNCEMENT_TONES = ['info', 'warning'] as const;
export type AnnouncementTone = (typeof ANNOUNCEMENT_TONES)[number];

export const ANNOUNCEMENT_AUDIENCES = [
  'all',
  'restaurant',
  'retail',
  'trial',
  'active',
  'merchants',
] as const;
export type AnnouncementAudience = (typeof ANNOUNCEMENT_AUDIENCES)[number];

export const ONBOARDING_DAYS = [1, 3, 7] as const;

const text = (max: number) => z.string().trim().min(1).max(max);

/** What a merchant user sees in the banner. */
export const merchantAnnouncementSchema = z.object({
  id: z.string(),
  titleAr: z.string(),
  titleEn: z.string(),
  bodyAr: z.string(),
  bodyEn: z.string(),
  tone: z.enum(ANNOUNCEMENT_TONES),
  startsAt: z.string(),
});
export type MerchantAnnouncement = z.infer<typeof merchantAnnouncementSchema>;

export const commsFeedSchema = z.object({
  announcements: z.array(merchantAnnouncementSchema),
  /** The NPS question is due for this user (BR-257: at most once per 30 days). */
  npsDue: z.boolean(),
});
export type CommsFeed = z.infer<typeof commsFeedSchema>;

/** A score 0–10 with an optional comment, or `score: null` to dismiss the question. */
export const npsAnswerSchema = z
  .object({
    score: z.number().int().min(0).max(10).nullable(),
    comment: z
      .string()
      .trim()
      .max(1000)
      .nullish()
      .transform((v) => (v == null || v === '' ? null : v)),
  })
  .refine((v) => v.score !== null || v.comment === null, {
    message: 'A comment needs a score.',
    path: ['comment'],
  });
export type NpsAnswer = z.input<typeof npsAnswerSchema>;

// ------------------------------------------------------------------------------ operators
export const announcementSchema = merchantAnnouncementSchema.extend({
  audience: z.enum(ANNOUNCEMENT_AUDIENCES),
  merchantIds: z.array(z.string()),
  endsAt: z.string().nullable(),
  sendEmail: z.boolean(),
  status: z.enum(['active', 'archived']),
  /** E-mails to owners so far (sent / failed / waiting). */
  emails: z.object({
    sent: z.number().int(),
    failed: z.number().int(),
    pending: z.number().int(),
  }),
  version: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Announcement = z.infer<typeof announcementSchema>;
export const announcementListSchema = z.object({ announcements: z.array(announcementSchema) });

export const saveAnnouncementSchema = z
  .object({
    titleAr: text(120),
    titleEn: text(120),
    bodyAr: text(2000),
    bodyEn: text(2000),
    tone: z.enum(ANNOUNCEMENT_TONES),
    audience: z.enum(ANNOUNCEMENT_AUDIENCES),
    merchantIds: z.array(z.uuid()).max(200).default([]),
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }).nullable(),
    sendEmail: z.boolean(),
    /** 0 when creating; the current version when editing (409 VERSION_CONFLICT otherwise). */
    version: z.number().int().nonnegative(),
  })
  .refine((v) => (v.audience === 'merchants') === v.merchantIds.length > 0, {
    message: 'Pick the merchants for this audience (and only for it).',
    path: ['merchantIds'],
  })
  .refine((v) => v.endsAt === null || new Date(v.endsAt) > new Date(v.startsAt), {
    message: 'The end must be after the start.',
    path: ['endsAt'],
  });
export type SaveAnnouncement = z.input<typeof saveAnnouncementSchema>;

export const audienceCountQuerySchema = z.object({
  audience: z.enum(ANNOUNCEMENT_AUDIENCES),
  merchantIds: z
    .string()
    .optional()
    .transform((v) => (v ? v.split(',').filter(Boolean) : []))
    .pipe(z.array(z.uuid()).max(200)),
});
export const audienceCountSchema = z.object({ merchants: z.number().int() });

export const onboardingStepSchema = z.object({
  day: z.number().int(),
  titleAr: z.string(),
  titleEn: z.string(),
  bodyAr: z.string(),
  bodyEn: z.string(),
  version: z.number().int(),
  updatedAt: z.string(),
});
export type OnboardingStep = z.infer<typeof onboardingStepSchema>;

export const onboardingSeriesSchema = z.object({
  enabled: z.boolean(),
  version: z.number().int(),
  steps: z.array(onboardingStepSchema),
  /** E-mails sent per day of the series so far. */
  sent: z.array(z.object({ day: z.number().int(), sent: z.number().int() })),
});
export type OnboardingSeries = z.infer<typeof onboardingSeriesSchema>;

export const saveOnboardingSettingsSchema = z.object({
  enabled: z.boolean(),
  version: z.number().int().positive(),
});
export type SaveOnboardingSettings = z.input<typeof saveOnboardingSettingsSchema>;

export const saveOnboardingStepSchema = z.object({
  titleAr: text(120),
  titleEn: text(120),
  bodyAr: text(2000),
  bodyEn: text(2000),
  version: z.number().int().positive(),
});
export type SaveOnboardingStep = z.input<typeof saveOnboardingStepSchema>;

export const npsReportQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(3650).default(90),
});

export const npsReportSchema = z.object({
  days: z.number().int(),
  responses: z.number().int(),
  promoters: z.number().int(),
  passives: z.number().int(),
  detractors: z.number().int(),
  dismissals: z.number().int(),
  /** Percentage of promoters minus percentage of detractors (−100…100), null with no answers. */
  score: z.number().int().nullable(),
  comments: z.array(
    z.object({
      id: z.string(),
      merchantId: z.string(),
      merchantNameEn: z.string().nullable(),
      merchantNameAr: z.string().nullable(),
      score: z.number().int(),
      comment: z.string(),
      createdAt: z.string(),
    }),
  ),
});
export type NpsReport = z.infer<typeof npsReportSchema>;
